/**
 * LearningTour — expanded coach-mark walkthrough for the Learning Catalog.
 * Covers not just the page chrome (nav, search) but the actual course
 * cards and their filters so members learn how to find + start a course.
 */
import React from 'react';
import PageTour from './PageTour';

const STEPS = [
  {
    target: '[data-testid="learning-page"]',
    title: 'Welcome to the classroom',
    body: 'This is where every course, module, and lesson lives. Everything is operator-vetted — no fluff, no filler.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="learning-subnav"]',
    title: 'Left navigation',
    body: 'Jump between the Board, Courses, Certificates, Streaks, and the AI Teacher planner. Sticky as you scroll — always one click away.',
    placement: 'right',
  },
  {
    target: '[data-testid="subnav-courses"]',
    title: 'The Courses tab',
    body: 'The main catalog. All courses you can take show here — free courses are open, higher tiers unlock more depth.',
    placement: 'right',
  },
  {
    target: '[data-testid="learning-tutorial-link"]',
    title: 'In-depth tutorial',
    body: 'Prefer a written guide? This link opens a full 17-section walkthrough with a table of contents you can revisit any time.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="learning-search"]',
    title: 'Search everything',
    body: 'Full-text search over titles, transcripts, and tags. Try "vacuum sensor" or "labeling" — you\'ll be surprised how much lives inside a lesson.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="tier-filter"]',
    title: 'Filter by membership tier',
    body: 'Show only Free, Pro, Business, or Enterprise courses. Locked courses always show a preview + upgrade CTA when clicked.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="status-filter-active"], [data-testid="status-filter-archived"]',
    title: 'Active vs Archived',
    body: 'Active courses are currently running cohorts (live events, office hours). Archived courses are evergreen — take them at your own pace, any time.',
    placement: 'bottom',
  },
  {
    target: '[data-testid^="course-card-"]',
    title: 'A course card',
    body: 'Each card shows the tier, track, module count, estimated hours, and a preview description. Hover for a summary; click to open the course.',
    placement: 'top',
  },
  {
    target: '[data-testid^="course-card-"] button, [data-testid^="course-card-"] a',
    title: 'Start or continue',
    body: 'The primary button on every card either says Start (never touched) or Continue (progress in flight). Clicking it drops you straight into the next lesson.',
    placement: 'top',
  },
  {
    target: '[data-testid="subnav-certificates"]',
    title: 'Certificates & badges',
    body: 'Finish every module quiz in a course to earn a verifiable, printable certificate with a unique ID. All your credentials live in this tab.',
    placement: 'right',
  },
  {
    target: '[data-testid="subnav-streaks"], [data-testid="subnav-roadmap"]',
    title: 'Streaks & Roadmap',
    body: 'Complete a lesson two days in a row and your streak kicks in. The Roadmap visualizes your path from your current tier to Master Practitioner.',
    placement: 'right',
  },
  {
    target: '[data-testid="ai-planner-open"]',
    title: 'AI Teacher planner',
    body: 'Answer three quick questions — goal, weekly hours, comfort with tech — and the AI drafts a personalized 14-day study plan pinned to your Dashboard.',
    placement: 'right',
  },
];

const LearningTour = ({ forceOpen, onFinish }) => (
  <PageTour
    steps={STEPS}
    storageKey="learning_tour_seen_v2"
    forceOpen={forceOpen}
    onFinish={onFinish}
  />
);

export default LearningTour;
